import type { GroupLayer, Layer } from './types';

/**
 * The layer tree.
 *
 * A document's layers are a tree: a top-level layer may be a group, and a
 * group's children may be groups too. Everything that walks the stack has to
 * say which view of the tree it wants, and this module is where those views
 * live:
 *
 *  - **the tree** — the document as stored. The Layers panel renders it.
 *  - **the leaves** — the layers that have pixels or glyphs, in z-order. The
 *    engine, the export, the merge and the transforms work on these.
 *  - **the siblings** — the layers next to each other inside one parent.
 *    Raising, lowering and merging are sibling operations, and a group is a
 *    sibling list of its own.
 *
 * All of it is pure: the tree is plain data, and every operation returns a new
 * tree rather than editing one in place.
 */

/** Is this layer a group? */
export function isGroup(layer: Layer): layer is GroupLayer {
  return layer.type === 'group';
}

/** A layer and the group context it sits in. */
export interface LeafContext {
  layer: Layer;
  /** False when an ancestor group is hidden — the leaf is not drawn. */
  visible: boolean;
  /** False when an ancestor group is locked — the leaf is drawn but not grabbed. */
  unlocked: boolean;
}

/**
 * Every leaf of the tree, in z-order, with the visibility and lock it inherits
 * from the groups above it.
 *
 * A group is invisible to everything that draws or grabs: the engine draws the
 * leaves, and the move tool grabs the leaves. The group contributes its name,
 * its visibility and its lock — nothing else.
 */
export function leafContexts(layers: Layer[]): LeafContext[] {
  const out: LeafContext[] = [];
  const walk = (siblings: Layer[], visible: boolean, unlocked: boolean) => {
    for (const layer of siblings) {
      if (isGroup(layer)) {
        walk(layer.children, visible && layer.visible, unlocked && !layer.locked);
        continue;
      }
      out.push({ layer, visible, unlocked });
    }
  };
  walk(layers, true, true);
  return out;
}

/** Every leaf of the tree, in z-order. */
export function leafLayers(layers: Layer[]): Layer[] {
  return leafContexts(layers).map((context) => context.layer);
}

/** Where a layer sits: the sibling list it belongs to and its index in it. */
export interface LayerLocation {
  /** The list the layer is in — a group's children, or the document's layers. */
  siblings: Layer[];
  index: number;
  /** The group that holds the list, or null for the top level. */
  parent: GroupLayer | null;
}

/** Find a layer anywhere in the tree, with the list it lives in. */
export function locateLayer(layers: Layer[], id: string): LayerLocation | null {
  const search = (siblings: Layer[], parent: GroupLayer | null): LayerLocation | null => {
    for (let index = 0; index < siblings.length; index += 1) {
      const layer = siblings[index];
      if (layer?.id === id) return { siblings, index, parent };
      if (layer && isGroup(layer)) {
        const found = search(layer.children, layer);
        if (found) return found;
      }
    }
    return null;
  };
  return search(layers, null);
}

/** Find a layer anywhere in the tree. */
export function findLayer(layers: Layer[], id: string): Layer | undefined {
  return locateLayer(layers, id)?.siblings.find((layer) => layer.id === id);
}

/** The group that holds a layer, or null when it is at the top level. */
export function findParent(layers: Layer[], id: string): GroupLayer | null {
  return locateLayer(layers, id)?.parent ?? null;
}

/**
 * Replace a layer's fields wherever it sits in the tree.
 *
 * Returns the original list when the id is not found, so a caller can commit
 * the result unconditionally.
 */
export function updateLayerIn<L extends Layer>(
  layers: Layer[],
  id: string,
  patch: Partial<L>,
): Layer[] {
  let changed = false;
  const walk = (siblings: Layer[]): Layer[] =>
    siblings.map((layer) => {
      if (layer.id === id) {
        changed = true;
        return { ...layer, ...patch } as Layer;
      }
      if (isGroup(layer)) {
        const children = walk(layer.children);
        if (children !== layer.children) {
          changed = true;
          return { ...layer, children };
        }
      }
      return layer;
    });
  const next = walk(layers);
  return changed ? next : layers;
}

/** Remove a layer from wherever it sits in the tree. */
export function removeLayerIn(layers: Layer[], id: string): Layer[] {
  let changed = false;
  const walk = (siblings: Layer[]): Layer[] => {
    const kept: Layer[] = [];
    for (const layer of siblings) {
      if (layer.id === id) {
        changed = true;
        continue;
      }
      if (isGroup(layer)) {
        const children = walk(layer.children);
        if (children !== layer.children) {
          changed = true;
          kept.push({ ...layer, children });
          continue;
        }
      }
      kept.push(layer);
    }
    return kept;
  };
  const next = walk(layers);
  return changed ? next : layers;
}

/** Replace the children of the group with the given id. */
export function replaceGroupChildren(layers: Layer[], groupId: string, children: Layer[]): Layer[] {
  return layers.map((layer) => {
    if (layer.id === groupId && isGroup(layer)) return { ...layer, children };
    if (isGroup(layer)) {
      return { ...layer, children: replaceGroupChildren(layer.children, groupId, children) };
    }
    return layer;
  });
}

/**
 * Put a new list of siblings back where they came from.
 *
 * The top level is the document's own list; anything deeper goes back into the
 * group that held it. Every other tree operation rebuilds the parents it
 * passes through, so this is the one place that has to know which list it is
 * replacing.
 */
export function replaceSiblings(layers: Layer[], location: LayerLocation, next: Layer[]): Layer[] {
  if (!location.parent) return next;
  return replaceGroupChildren(layers, location.parent.id, next);
}

/** Copy a layer and everything inside it, with fresh ids throughout. */
export function copyLayerTree(layer: Layer, makeId: () => string): Layer {
  if (!isGroup(layer)) return { ...layer, id: makeId() } as Layer;
  return {
    ...layer,
    id: makeId(),
    children: layer.children.map((child) => copyLayerTree(child, makeId)),
  } as Layer;
}

/**
 * Move a layer — and everything inside it — by a delta.
 *
 * A group has no position of its own, so the delta goes on its children: the
 * group's contents move, and the group follows them.
 */
export function offsetLayerTree(layer: Layer, dx: number, dy: number): Layer {
  if (isGroup(layer)) {
    return {
      ...layer,
      children: layer.children.map((child) => offsetLayerTree(child, dx, dy)),
    } as Layer;
  }
  return { ...layer, x: layer.x + dx, y: layer.y + dy } as Layer;
}

/**
 * Remove leaves by id, and drop any group left empty.
 *
 * A group with nothing in it is not a folder any more — it is a row in the
 * layer list that does nothing — so it goes with its last child.
 */
export function removeLeaves(layers: Layer[], ids: ReadonlySet<string>): Layer[] {
  const out: Layer[] = [];
  for (const layer of layers) {
    if (!isGroup(layer)) {
      if (!ids.has(layer.id)) out.push(layer);
      continue;
    }
    const children = removeLeaves(layer.children, ids);
    if (children.length === 0) continue;
    out.push({ ...layer, children });
  }
  return out;
}

/** The id of the outermost ancestor of a layer — itself, when it is at the top. */
export function topLevelAncestorId(layers: Layer[], id: string): string | null {
  for (const layer of layers) {
    if (layer.id === id) return layer.id;
    if (isGroup(layer)) {
      const found = topLevelAncestorId(layer.children, id);
      if (found) return layer.id;
    }
  }
  return null;
}

/**
 * The group that *directly* holds `id`, or null when it sits at the top level.
 *
 * Where {@link topLevelAncestorId} answers "which top-level slot", this answers
 * "which sibling list" — the question the composite rules ask, since a group's
 * opacity gates exactly its own children's list.
 */
export function parentGroup(layers: Layer[], id: string): GroupLayer | null {
  for (const layer of layers) {
    if (!isGroup(layer)) continue;
    if (layer.children.some((child) => child.id === id)) return layer;
    const nested = parentGroup(layer.children, id);
    if (nested) return nested;
  }
  return null;
}

/**
 * Insert a layer into the list that holds `referenceId`.
 *
 * With a reference, the new layer lands next to it in the same list — inside
 * the same group, when the reference is inside one. Without one, it goes on
 * top of the top-level stack.
 */
export function insertNextTo(
  layers: Layer[],
  layer: Layer,
  referenceId: string | null,
  offset: 1 | -1 = 1,
): Layer[] {
  if (!referenceId) return [...layers, layer];
  const location = locateLayer(layers, referenceId);
  if (!location) return [...layers, layer];
  const at = Math.max(0, Math.min(location.index + offset, location.siblings.length));
  const next = [...location.siblings];
  next.splice(at, 0, layer);
  return replaceSiblings(layers, location, next);
}

/** How many leaves the tree has — the number the Layers panel counts. */
export function countLeaves(layers: Layer[]): number {
  return leafLayers(layers).length;
}

/** Every layer in the tree, groups included, in z-order. */
export function allLayers(layers: Layer[]): Layer[] {
  const out: Layer[] = [];
  const walk = (siblings: Layer[]) => {
    for (const layer of siblings) {
      out.push(layer);
      if (isGroup(layer)) walk(layer.children);
    }
  };
  walk(layers);
  return out;
}

/**
 * The ids in the order the Layers panel draws them — top-most first, a group's
 * children directly under its row.
 *
 * Range selection spans this list: Shift-click selects the rows between the
 * anchor and the target as drawn, through group indentation, whether or not a
 * group happens to be expanded (a collapsed group row stands for its children).
 */
export function rowOrder(layers: Layer[]): string[] {
  const out: string[] = [];
  const walk = (siblings: Layer[]) => {
    for (let index = siblings.length - 1; index >= 0; index -= 1) {
      const layer = siblings[index];
      if (!layer) continue;
      out.push(layer.id);
      if (isGroup(layer)) walk(layer.children);
    }
  };
  walk(layers);
  return out;
}

/**
 * Raise or lower the selected layers among their siblings, as blocks.
 *
 * Each maximal run of consecutive selected siblings shifts one slot in
 * `direction` (+1 towards the top of the stored list, which the panel draws as
 * up), keeping its internal order. Moving per layer would interleave selected
 * and unselected siblings and scramble the set the user built; a block is what
 * the drag metaphor promises. Runs at the list's edge cannot move, like a
 * single layer at the top; that is a no-op for them, not an error.
 *
 * Runs are applied bottom-first when raising and top-first when lowering, so
 * the index arithmetic never sees the list it has already shifted. Returns the
 * original list when nothing moved, so a caller can commit unconditionally.
 */
export function moveSiblingRuns(
  siblings: Layer[],
  selectedIds: ReadonlySet<string>,
  direction: 1 | -1,
): Layer[] {
  const selected = (index: number): boolean => {
    const layer = siblings[index];
    return layer !== undefined && selectedIds.has(layer.id);
  };

  // Maximal runs, as [start, end] index pairs in the stored (bottom-first) order.
  const runs: [number, number][] = [];
  let start = -1;
  for (let index = 0; index <= siblings.length; index += 1) {
    const inRun = index < siblings.length && selected(index);
    if (inRun && start < 0) start = index;
    if (!inRun && start >= 0) {
      runs.push([start, index - 1]);
      start = -1;
    }
  }
  if (runs.length === 0) return siblings;

  const next = [...siblings];
  let changed = false;
  if (direction === 1) {
    // The sibling above each run (index end+1) slides below it.
    for (let r = runs.length - 1; r >= 0; r -= 1) {
      const run = runs[r];
      if (!run) continue;
      const [from, to] = run;
      if (to + 1 >= next.length) continue;
      const [mover] = next.splice(to + 1, 1);
      if (mover === undefined) continue;
      next.splice(from, 0, mover);
      changed = true;
    }
  } else {
    // The sibling below each run (index from-1) slides above it.
    for (const run of runs) {
      if (!run) continue;
      const [from, to] = run;
      if (from - 1 < 0) continue;
      const [mover] = next.splice(from - 1, 1);
      if (mover === undefined) continue;
      next.splice(to, 0, mover);
      changed = true;
    }
  }
  return changed ? next : siblings;
}

/**
 * Remove whole subtrees by id — leaves, and groups with everything inside them.
 *
 * Delete-over-set removes what the user selected, and a selected group is
 * selected *with* its contents. A group none of whose children survive is not
 * a folder any more (the same rule `removeLeaves` applies), so it goes too.
 */
export function pruneSubtrees(layers: Layer[], ids: ReadonlySet<string>): Layer[] {
  let changed = false;
  const walk = (siblings: Layer[]): Layer[] => {
    const kept: Layer[] = [];
    let touched = false;
    for (const layer of siblings) {
      if (ids.has(layer.id)) {
        touched = true;
        continue;
      }
      if (isGroup(layer)) {
        const children = walk(layer.children);
        // Only a group the pruning emptied goes — an originally empty folder
        // survives a no-op call untouched.
        if (children.length === 0 && children !== layer.children) {
          touched = true;
          continue;
        }
        if (children !== layer.children) kept.push({ ...layer, children });
        else kept.push(layer);
        continue;
      }
      kept.push(layer);
    }
    if (touched) changed = true;
    return touched ? kept : siblings;
  };
  const next = walk(layers);
  return changed ? next : layers;
}
